import { useEffect, useState } from "react";
import { CalendarDays, Clock, MapPin, ExternalLink } from "lucide-react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import SEO from "../../components/SEO";
import { PageHero, EmptyState, LazyImg, HtmlBlock } from "../../components/Bits";

const STATUS_CLS = { upcoming: "bg-brandgreen-100 text-brandgreen", ongoing: "bg-saffron-100 text-saffron-600", completed: "bg-navy-50 text-slate-500", past: "bg-slate-100 text-slate-500" };

export default function News() {
  const { t, lang } = useApp();
  const [data, setData] = useState({ items: [] });
  const [type, setType] = useState("");

  useEffect(() => {
    api.get(`/public/news?limit=60${type ? `&type=${type}` : ""}`).then((r) => setData(r.data)).catch(() => {});
  }, [type]);

  const news = data.items.filter((n) => n.type !== "event");
  const events = data.items.filter((n) => n.type === "event");
  const upcoming = events.filter((e) => e.event_status !== "past");
  const past = events.filter((e) => e.event_status === "past");
  const showEvents = type !== "news";
  const showNews = type !== "event";

  const Card = ({ n, dim }) => (
    <article className={`card-ssf flex flex-col overflow-hidden ${dim ? "opacity-90" : ""}`} data-testid={`news-card-${n.id}`}>
      {n.image && <LazyImg src={n.image} alt={n.alt_text || n.title} className="aspect-[16/9] w-full object-cover" />}
      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_CLS[n.event_status] || STATUS_CLS.upcoming}`} data-testid={`news-status-${n.id}`}>{n.event_status === "past" ? (lang === "hi" ? "पूर्व कार्यक्रम" : "Past Event") : t(n.event_status || "upcoming")}</span>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{n.type}</span>
        </div>
        <h2 className="mt-3 font-display text-lg font-bold text-navy">{lang === "hi" && n.title_hi ? n.title_hi : n.title}</h2>
        <HtmlBlock html={lang === "hi" && n.description_hi ? n.description_hi : n.description} className="mt-2 flex-1 text-sm leading-relaxed text-slate-600" testid={`news-desc-${n.id}`} />
        <div className="mt-4 space-y-1 text-xs text-slate-500">
          {n.date && <p className="flex items-center gap-1.5"><CalendarDays size={13} />{n.date} {(n.time || n.start_time) && <span className="inline-flex items-center gap-1"><Clock size={13} />{[n.start_time, n.end_time].filter(Boolean).join(" – ") || n.time}</span>}</p>}
          {n.venue && <p className="flex items-center gap-1.5"><MapPin size={13} />{n.venue}</p>}
        </div>
        {n.reg_link && n.event_status !== "past" && (
          <a href={n.reg_link} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-saffron-600 hover:text-navy" data-testid={`news-reg-${n.id}`}>
            {t("register")} <ExternalLink size={13} />
          </a>
        )}
      </div>
    </article>
  );

  return (
    <div data-testid="news-page">
      <SEO title={t("news")} />
      <PageHero title={t("news")} sub={lang === "hi" ? "फाउंडेशन की खबरें और कार्यक्रम।" : "Announcements, programmes and events from the Foundation."} />
      <section className="py-16">
        <div className="container-ssf">
          <div className="mb-8 flex gap-2">
            {["", "news", "event"].map((v) => (
              <button key={v} onClick={() => setType(v)} data-testid={`news-filter-${v || "all"}`}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${type === v ? "bg-navy text-white" : "bg-white text-navy border border-gray-200 hover:border-saffron-500"}`}>
                {v === "" ? (lang === "hi" ? "सभी" : "All") : t(v === "news" ? "newsLabel" : "event")}
              </button>
            ))}
          </div>
          {data.items.length === 0 && <EmptyState text="News and events will appear here once published." />}

          {showNews && news.length > 0 && (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{news.map((n) => <Card key={n.id} n={n} />)}</div>
          )}

          {showEvents && upcoming.length > 0 && (
            <>
              <h2 className="mb-6 mt-4 font-display text-2xl font-bold text-navy" data-testid="upcoming-events-heading">{lang === "hi" ? "आगामी कार्यक्रम" : "Upcoming Events"}</h2>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{upcoming.map((n) => <Card key={n.id} n={n} />)}</div>
            </>
          )}

          {showEvents && past.length > 0 && (
            <>
              <h2 className="mb-6 mt-14 font-display text-2xl font-bold text-slate-500" data-testid="past-events-heading">{lang === "hi" ? "पूर्व कार्यक्रम" : "Past Events"}</h2>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{past.map((n) => <Card key={n.id} n={n} dim />)}</div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
